import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { formatEuro, MOIS_LABELS } from '../../utils/calculs'

const MODES = [
  { key: 'cb_pin', label: 'CB pin' },
  { key: 'cb_sans_contact', label: 'CB sans contact' },
  { key: 'cheque_comptant', label: 'Chèque comptant' },
  { key: 'cheque_differe', label: 'Chèque différé' },
  { key: 'especes', label: 'Espèces' },
]

const INIT_FORM = { cb_pin: '', cb_sans_contact: '', cheque_comptant: '', cheque_differe: '', especes: '', notes: '' }

// Fix bug UTC : formatage local explicite (jamais toISOString sur une date locale)
function formatLocalDate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getMonday(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  return formatLocalDate(d)
}

function getSunday(monday) {
  const d = new Date(monday)
  d.setDate(d.getDate() + 6)
  return formatLocalDate(d)
}

export default function SuiviFinancier() {
  const { profile } = useAuth()
  const now = new Date()
  const [vue, setVue] = useState('saisie')
  const [dateDebut, setDateDebut] = useState(getMonday(now))
  const [dateFin, setDateFin] = useState(getSunday(getMonday(now)))
  const [form, setForm] = useState({ ...INIT_FORM })
  const [saisieExistante, setSaisieExistante] = useState(null)
  const [editMode, setEditMode] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  // Recap mensuel
  const [annee, setAnnee] = useState(now.getFullYear())
  const [mois, setMois] = useState(now.getMonth() + 1)
  const [saisiesMois, setSaisiesMois] = useState([])
  const [loadingRecap, setLoadingRecap] = useState(false)

  // Quand on change les dates, on cherche une saisie existante sur cette période
  useEffect(() => {
    async function check() {
      setMsg(null)
      const { data } = await supabase.from('saisies_financieres')
        .select('*').eq('date_debut', dateDebut).eq('date_fin', dateFin).maybeSingle()
      if (data) {
        setSaisieExistante(data)
        setForm({
          cb_pin: data.cb_pin || '',
          cb_sans_contact: data.cb_sans_contact || '',
          cheque_comptant: data.cheque_comptant || '',
          cheque_differe: data.cheque_differe || '',
          especes: data.especes || '',
          notes: data.notes || ''
        })
        setEditMode(false)
      } else {
        setSaisieExistante(null)
        setForm({ ...INIT_FORM })
        setEditMode(true)
      }
    }
    if (dateDebut && dateFin) check()
  }, [dateDebut, dateFin])

  // Sync fin quand début change (si on change le début, on recalcule la semaine)
  function handleDateDebut(val) {
    setDateDebut(val)
    setDateFin(getSunday(val))
  }

  const loadRecap = useCallback(async () => {
    setLoadingRecap(true)
    const debut = `${annee}-${String(mois).padStart(2,'0')}-01`
    const fin = formatLocalDate(new Date(annee, mois, 0))
    const { data } = await supabase.from('saisies_financieres')
      .select('*')
      .gte('date_debut', debut)
      .lte('date_debut', fin)
      .order('date_debut')
    setSaisiesMois(data || [])
    setLoadingRecap(false)
  }, [annee, mois])

  useEffect(() => { if (vue === 'recap') loadRecap() }, [vue, loadRecap])

  const total = MODES.reduce((a, m) => a + (parseFloat(form[m.key]) || 0), 0)

  async function handleSave() {
    setSaving(true)
    setMsg(null)
    const payload = {
      date_debut: dateDebut,
      date_fin: dateFin,
      cb_pin: parseFloat(form.cb_pin) || 0,
      cb_sans_contact: parseFloat(form.cb_sans_contact) || 0,
      cheque_comptant: parseFloat(form.cheque_comptant) || 0,
      cheque_differe: parseFloat(form.cheque_differe) || 0,
      especes: parseFloat(form.especes) || 0,
      notes: form.notes || null,
      cree_par: profile.id
    }

    let error
    if (saisieExistante) {
      const { error: e } = await supabase.from('saisies_financieres').update(payload).eq('id', saisieExistante.id)
      error = e
    } else {
      const { error: e } = await supabase.from('saisies_financieres').insert(payload)
      error = e
    }

    if (error) {
      setMsg({ type: 'error', text: error.message })
    } else {
      setMsg({ type: 'success', text: 'Saisie enregistrée.' })
      const { data } = await supabase.from('saisies_financieres')
        .select('*').eq('date_debut', dateDebut).eq('date_fin', dateFin).single()
      setSaisieExistante(data)
      setEditMode(false)
    }
    setSaving(false)
  }

  // Totaux récap mensuel
  const totauxRecap = saisiesMois.reduce((acc, s) => {
    for (const m of MODES) acc[m.key] = (acc[m.key] || 0) + (s[m.key] || 0)
    acc.total = (acc.total || 0) + MODES.reduce((a, m) => a + (s[m.key] || 0), 0)
    return acc
  }, {})

  const annees = [2024, 2025, 2026, 2027]

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Suivi financier</h2>
          <div className="page-header-sub">Encaissements par mode de paiement</div>
        </div>
      </div>

      <div className="tabs" style={{marginBottom:24}}>
        <button className={`tab-btn ${vue === 'saisie' ? 'active' : ''}`} onClick={() => setVue('saisie')}>Saisie</button>
        <button className={`tab-btn ${vue === 'recap' ? 'active' : ''}`} onClick={() => setVue('recap')}>Récapitulatif mensuel</button>
      </div>

      {vue === 'saisie' ? (
        <>
          {/* Sélecteur de période */}
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap'}}>
              <div className="form-group" style={{margin:0}}>
                <label>Date de début</label>
                <input type="date" value={dateDebut} onChange={e => handleDateDebut(e.target.value)} />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Date de fin</label>
                <input type="date" value={dateFin} min={dateDebut}
                  onChange={e => setDateFin(e.target.value)} />
              </div>
              <div style={{fontSize:12, color:'var(--stone)', paddingBottom:8}}>
                {dateDebut && dateFin && (
                  <>Du {new Date(dateDebut).toLocaleDateString('fr-FR')} au {new Date(dateFin).toLocaleDateString('fr-FR')}</>
                )}
              </div>
            </div>
          </div>

          {saisieExistante && !editMode && (
            <div className="alert alert-info" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
              <span>Saisie déjà enregistrée pour cette période.</span>
              <button className="btn btn-outline btn-sm" onClick={() => setEditMode(true)}>Modifier</button>
            </div>
          )}

          {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

          <div style={{display:'grid', gridTemplateColumns:'1fr 300px', gap:20, alignItems:'start'}}>
            <div className="card">
              <h3 style={{marginBottom:20}}>Encaissements</h3>
              <table>
                <thead>
                  <tr>
                    <th>Mode de paiement</th>
                    <th className="num">Montant (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {MODES.map(m => (
                    <tr key={m.key}>
                      <td>{m.label}</td>
                      <td className="num">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form[m.key]}
                          onChange={e => setForm(f => ({ ...f, [m.key]: e.target.value }))}
                          disabled={!editMode}
                          className="nb-input"
                          style={{width:120}}
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="divider" />
              <div className="form-group">
                <label>Notes (optionnel)</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  disabled={!editMode}
                  rows={2}
                  style={{padding:'8px 11px', border:'1px solid var(--border)', borderRadius:'var(--radius)', resize:'vertical', fontFamily:'Inter, sans-serif', fontSize:13}}
                  placeholder="Observations, écarts de caisse…"
                />
              </div>
            </div>

            <div className="card">
              <h3 style={{marginBottom:20}}>Récapitulatif</h3>
              {MODES.map(m => (
                <div key={m.key} className="recap-line">
                  <span>{m.label}</span>
                  <strong>{formatEuro(parseFloat(form[m.key]) || 0)}</strong>
                </div>
              ))}
              <div className="recap-highlight" style={{marginTop:8}}>
                <div className="recap-line" style={{border:'none', padding:0}}>
                  <span>Total encaissé</span>
                  <strong>{formatEuro(total)}</strong>
                </div>
              </div>

              {editMode && (
                <button className="btn btn-bordeaux" style={{width:'100%', marginTop:20}}
                  onClick={handleSave} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{display:'flex', gap:12, marginBottom:24}}>
            <select value={annee} onChange={e => setAnnee(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={mois} onChange={e => setMois(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {MOIS_LABELS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>

          {/* Stats cards */}
          <div className="stats-grid" style={{marginBottom:24}}>
            <div className="stat-card">
              <div className="s-label">Total encaissé</div>
              <div className="s-value">{formatEuro(totauxRecap.total || 0)}</div>
              <div className="s-sub">{saisiesMois.length} période{saisiesMois.length > 1 ? 's' : ''}</div>
            </div>
            {MODES.slice(0, 2).map(m => (
              <div key={m.key} className="stat-card">
                <div className="s-label">{m.label}</div>
                <div className="s-value">{formatEuro(totauxRecap[m.key] || 0)}</div>
              </div>
            ))}
            <div className="stat-card">
              <div className="s-label">Chèques + Espèces</div>
              <div className="s-value">{formatEuro((totauxRecap.cheque_comptant || 0) + (totauxRecap.cheque_differe || 0) + (totauxRecap.especes || 0))}</div>
            </div>
          </div>

          {loadingRecap ? <div className="spinner" /> : (
            <div className="card" style={{overflowX:'auto'}}>
              <table>
                <thead>
                  <tr>
                    <th>Période</th>
                    {MODES.map(m => <th key={m.key} className="num">{m.label}</th>)}
                    <th className="num">Total</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {saisiesMois.length === 0 ? (
                    <tr><td colSpan={8} style={{textAlign:'center', color:'var(--stone)', padding:32}}>
                      Aucune saisie pour {MOIS_LABELS[mois]} {annee}
                    </td></tr>
                  ) : saisiesMois.map(s => {
                    const tot = MODES.reduce((a, m) => a + (s[m.key] || 0), 0)
                    return (
                      <tr key={s.id}>
                        <td style={{whiteSpace:'nowrap', fontSize:12}}>
                          {new Date(s.date_debut).toLocaleDateString('fr-FR', {day:'2-digit', month:'2-digit'})}
                          {' → '}
                          {new Date(s.date_fin).toLocaleDateString('fr-FR', {day:'2-digit', month:'2-digit'})}
                        </td>
                        {MODES.map(m => (
                          <td key={m.key} className="num">
                            {s[m.key] > 0 ? formatEuro(s[m.key]) : <span className="text-muted">—</span>}
                          </td>
                        ))}
                        <td className="num"><strong>{formatEuro(tot)}</strong></td>
                        <td style={{fontSize:12, color:'var(--stone)'}}>{s.notes || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
                {saisiesMois.length > 0 && (
                  <tfoot>
                    <tr>
                      <td><strong>Total {MOIS_LABELS[mois]}</strong></td>
                      {MODES.map(m => (
                        <td key={m.key} className="num">
                          <strong>{totauxRecap[m.key] > 0 ? formatEuro(totauxRecap[m.key]) : '—'}</strong>
                        </td>
                      ))}
                      <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totauxRecap.total || 0)}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
